/**
 * useTrackRecsView — fires CIO `recommendation_view` once a recs carousel is
 * half visible (SFCC-11961). Companion to `useTrackRecsClick`: the click closes
 * the attribution loop, the view gives CIO the impression denominator. Neither
 * the beacon nor the SDK sent it before (Constructor tracking capture).
 *
 * Attach the returned callback ref to the carousel root — a callback so a pod
 * that mounts later (lazy chunk, dropdown opening) is still observed. Fires
 * once per distinct pod / result set (a new `result_id` re-fires) per mount of
 * the observed element, so a pod shown again (dropdown re-opened) counts
 * again. No-op while the
 * `Constructor_SdkTrackingEnabled` cutover switch is off, during SSR, or before
 * the SDK loads.
 */
import { useEffect, useRef, useState } from "react";

import { safeFireCioBeacon } from "~/hec/analytics/cio-listing-beacons";
import { useCioSdkTracker } from "~/hec/analytics/cio-sdk-tracking";

interface TrackRecsViewArgs {
  podId?: string;
  /** From the top-level `result_id` field on the CIO response. */
  resultId?: string;
  /** Ids of the rendered tiles, in carousel order. */
  itemIds: string[];
  /** Total results the pod returned, when known. */
  resultCount?: number;
  /** CIO index section of the pod's items; defaults to "Products". */
  section?: string;
}

/** Share of the carousel that must be on screen to count as viewed. */
const VIEW_THRESHOLD = 0.5;

export function useTrackRecsView<T extends HTMLElement = HTMLDivElement>({
  podId,
  resultId,
  itemIds,
  resultCount,
  section = "Products",
}: TrackRecsViewArgs) {
  const cio = useCioSdkTracker();
  const [el, setEl] = useState<T | null>(null);
  const firedKey = useRef<string | null>(null);

  // Encodes every value the observer sends, so it never fires stale ones.
  const key =
    cio && podId && itemIds.length
      ? `${podId}|${resultId ?? ""}|${section}|${resultCount ?? ""}|${itemIds.join(",")}`
      : null;

  // The element unmounted (e.g. dropdown closed): the next showing is a new view.
  useEffect(() => {
    if (!el) firedKey.current = null;
  }, [el]);

  useEffect(() => {
    if (
      !key ||
      !cio ||
      !podId ||
      !el ||
      firedKey.current === key ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        // `isIntersecting` is true for any overlap (even 0px edge contact) and
        // the first entry always arrives, so the ratio must be checked too.
        const viewed = entries.some(
          entry =>
            entry.isIntersecting && entry.intersectionRatio >= VIEW_THRESHOLD,
        );
        if (!viewed) return;
        observer.disconnect();
        firedKey.current = key;
        safeFireCioBeacon(() => {
          cio.tracker.trackRecommendationView({
            url: window.location.href,
            podId,
            numResultsViewed: itemIds.length,
            items: itemIds.map(itemId => ({ itemId })),
            resultCount,
            resultId,
            section,
          });
        });
      },
      { threshold: VIEW_THRESHOLD },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // `key` encodes every sent value; `cio` is the singleton (null → set flips `key`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, el]);

  return setEl;
}
